import { intersects2, unionBbox2, type Bbox2 } from "../geo/bbox";
import type { GeoRaster } from "../io/geoRaster";
import type { DatasetSource } from "../ui/sources";

/** One TIFF loaded into the plan-print stack. */
export interface StackLayer {
  id: string;
  raster: GeoRaster;
  source: DatasetSource;
  visible: boolean;
}

/**
 * An ordered stack of independently loaded rasters, painted bottom-to-top.
 *
 * There is no per-pixel blending decision to make: where two rasters' footprints overlap,
 * whichever is later in `layers` simply paints over the other (see `PlanRenderer`). "Picking
 * the one you want" is therefore just reordering the stack — `bringToFront` — rather than any
 * kind of masking or clipping.
 */
export class OrthoStack {
  private layers: StackLayer[] = [];

  get all(): readonly StackLayer[] {
    return this.layers;
  }

  get isEmpty(): boolean {
    return this.layers.length === 0;
  }

  add(raster: GeoRaster, source: DatasetSource): StackLayer {
    const layer: StackLayer = { id: crypto.randomUUID(), raster, source, visible: true };
    this.layers.push(layer);
    return layer;
  }

  remove(id: string): void {
    const layer = this.layers.find((l) => l.id === id);
    layer?.raster.dispose();
    this.layers = this.layers.filter((l) => l.id !== id);
  }

  setVisible(id: string, visible: boolean): void {
    const layer = this.layers.find((l) => l.id === id);
    if (layer) layer.visible = visible;
  }

  /** Moves a layer to the top of the paint order. */
  bringToFront(id: string): void {
    const index = this.layers.findIndex((l) => l.id === id);
    if (index < 0 || index === this.layers.length - 1) return;
    const [layer] = this.layers.splice(index, 1);
    this.layers.push(layer);
  }

  /** True when this layer's footprint overlaps another *visible* layer's. */
  overlapsAnother(id: string): boolean {
    const layer = this.layers.find((l) => l.id === id);
    if (!layer) return false;
    return this.layers.some(
      (other) => other.id !== id && other.visible && intersects2(layer.raster.bbox, other.raster.bbox),
    );
  }

  /** Bottom-to-top paint order, visible layers only. */
  paintOrder(): StackLayer[] {
    return this.layers.filter((l) => l.visible);
  }

  /** Combined footprint of every loaded layer (visible or not), for framing/export defaults. */
  extent(): Bbox2 | null {
    if (this.layers.length === 0) return null;
    return this.layers.reduce<Bbox2>((acc, l) => unionBbox2(acc, l.raster.bbox), { ...this.layers[0].raster.bbox });
  }

  dispose(): void {
    for (const layer of this.layers) layer.raster.dispose();
    this.layers = [];
  }
}
